import { useCallback } from 'react';
import {
  useCreateRecord,
  useDeleteFile,
  useFindRecord,
  useInvalidateQueries,
  usePopulate,
  useUpdateRecord,
  useUploadFile,
} from 'api/APIHooks';
import Notifier from 'src/common/helpers/Notifier';
import { useFeeScheduleProgramMetafields } from 'common/hooks/useFeeScheduleProgramMetafields';

const notifyOfSuccess = (newProvidedServiceState) => {
  const successMessage = newProvidedServiceState === 'active' ?
    'Your contracted service has been saved' :
    'Your submission request is in queue and processing. View the progress in Invoices.';
  Notifier.dispatch('success', successMessage);
};

const useFSPMetaFiles = (fspId) => {
  const { metafields, isSuccess } = useFeeScheduleProgramMetafields(fspId);
  return {
    metafieldFiles: metafields.filter(({ type }) => (type === 'file')),
    isSuccess,
  };
};

const useFindExistingProvidedService = (providedServiceId) => {
  const { data, isSuccess: isPSSuccess } = useFindRecord(
    'provided_service',
    providedServiceId,
    { queryConfig: { placeholderData: undefined, enabled: !!providedServiceId } },
  );
  const existingProvidedService = data?.data?.data;
  const { isSuccess: isProgramSuccess } = usePopulate(
    'program',
    'program',
    existingProvidedService,
    { queryConfig: { placeholderData: undefined } },
  );

  return {
    existingProvidedService,
    isSuccess: isPSSuccess && isProgramSuccess,
  };
};

const useUpdatePS = () => {
  const invalidateQuery = useInvalidateQueries();
  const { updateRecord } = useUpdateRecord('provided_service', {
    mutationConfig: {
      onError: () => Notifier.dispatch(
        'error',
        "We've encountered an unexpected problem saving the contracted service. Please update the draft and resubmit.",
      ),
      onSettled: () => {
        invalidateQuery('file_upload');
        invalidateQuery('provided_service');
      },
    },
  });
  return updateRecord;
};

const sortFiles = (files = [], existingFiles = []) => {
  const filesToCreate = [];
  const fileIdsToMaintain = [];
  files.forEach((file) => {
    const isNew = !!file.tempId;
    if (isNew) {
      filesToCreate.push(file.file);
    } else {
      fileIdsToMaintain.push(file.id);
    }
  });
  const fileIdsToDelete = existingFiles.filter(
    (oldId) => !files.some(({ id: newId }) => newId === oldId),
  ) ?? [];

  return {
    filesToCreate,
    fileIdsToMaintain,
    fileIdsToDelete,
  };
};

const useChangeFiles = (existingMetadata = []) => {
  const { uploadFile: createFileUpload } = useUploadFile('provided_service');
  const { deleteFile: deleteFileUpload } = useDeleteFile('provided_service');

  return {
    changeFiles: async (providedServiceId, fileMetadata) => {
      const failedFileResults = [];
      const fileMetadataAfterUploads = await Promise.all(
        fileMetadata.flatMap(async ({ value: files = [], field }) => {
          const existingFiles = existingMetadata.find((md) => md.field === field)?.value;
          const { filesToCreate, fileIdsToMaintain, fileIdsToDelete } = sortFiles(files, existingFiles);
          const areFilesUpdated = filesToCreate.length || fileIdsToDelete.length;

          const successfulUploadIds = [];
          let fileIdsThatFailedToDelete = [];
          if (areFilesUpdated) {
            const createResults = await Promise.allSettled(
              filesToCreate.map(async (file) => createFileUpload(
                providedServiceId,
                file,
              )),
            );
            const deleteResults = await Promise.allSettled(
              fileIdsToDelete.map(async (fileId) => deleteFileUpload(
                providedServiceId,
                fileId,
              )),
            );

            createResults.forEach((result) => {
              if (result?.status === 'fulfilled') {
                successfulUploadIds.push(result.value.data.data.id);
              } else {
                failedFileResults.push(result);
              }
            });
            failedFileResults.push(...deleteResults.filter((result) => result?.status === 'rejected'));
            fileIdsThatFailedToDelete = fileIdsToDelete.filter((_fileId, i) => deleteResults[i]?.status === 'rejected');
          }

          return {
            field,
            value: [...fileIdsToMaintain, ...successfulUploadIds, ...fileIdsThatFailedToDelete],
          };
        }),
      );

      return { fileMetadataAfterUploads, failedFileResults };
    },
  };
};

const sortMetadata = (metadata = [], metafieldFiles = []) => {
  const fileMetadata = [];
  const otherMetadata = [];
  metadata.forEach((md) => {
    const isFile = metafieldFiles.some((mf) => mf.field === md.field);
    if (isFile) {
      fileMetadata.push(md);
    } else {
      otherMetadata.push(md);
    }
  });
  return {
    fileMetadata,
    otherMetadata,
  };
};

const useUpdatePSIncludingFileUploads = (metafieldFiles, existingMetadata = []) => {
  const { changeFiles } = useChangeFiles(existingMetadata);
  const updatePS = useUpdatePS();

  return useCallback(async (updatedProvidedService) => {
    const updatedMetadata = updatedProvidedService.metadata;
    const isMetadataUpdated = !!updatedMetadata;

    const { fileMetadata, otherMetadata } = sortMetadata(updatedMetadata, metafieldFiles);
    const { fileMetadataAfterUploads, failedFileResults } = await changeFiles(updatedProvidedService.id, fileMetadata);

    const didAnyFileChangesFail = failedFileResults.length > 0;
    const requestMetadata = isMetadataUpdated ? [
      ...otherMetadata,
      ...fileMetadataAfterUploads,
    ] : undefined;

    const intendedState = updatedProvidedService.state;
    // don't allow changing state (submitting) if any file changes failed
    // the user must address them before submitting
    const requestState = didAnyFileChangesFail ? undefined : intendedState;

    const updateResponse = await updatePS(
      updatedProvidedService.id,
      {
        ...updatedProvidedService,
        id: undefined,
        metadata: requestMetadata,
        state: requestState,
      },
    );

    if (didAnyFileChangesFail) {
      Notifier.dispatch(
        'error',
        'There was a problem changing your file(s). Your contracted service has been saved. ' +
        'Try uploading/deleting your file(s) again.',
      );
    } else {
      notifyOfSuccess(intendedState);
    }

    return updateResponse;
  }, [updatePS, metafieldFiles, changeFiles, existingMetadata]);
};

export const useCreateProvidedService = (fspId) => {
  const invalidateQuery = useInvalidateQueries();
  const { metafieldFiles, isSuccess: isFSPSuccess } = useFSPMetaFiles(fspId);
  const { createRecord: createProvidedService } = useCreateRecord(
    'provided_service',
    {
      mutationConfig: {
        onError: () => Notifier.dispatch(
          'error',
          'We\'ve encountered an unexpected problem creating the contracted service. Please try again.',
        ),
      },
    },
  );
  const updatePSIncludingFileUploads = useUpdatePSIncludingFileUploads(metafieldFiles);

  return {
    isReady: isFSPSuccess,
    createProvidedService: useCallback(async (wouldBeProvidedService) => {
      const intendedState = wouldBeProvidedService.state;

      const metadata = wouldBeProvidedService.metadata ?? [];
      const { fileMetadata, otherMetadata } = sortMetadata(metadata, metafieldFiles);
      const isFileCreationNeeded = !!fileMetadata.length;

      const truthyAttrs = Object.entries(wouldBeProvidedService).reduce(
        (acc, [wouldBeKey, wouldBeVal]) => ({
          ...acc,
          [wouldBeKey]: wouldBeVal || undefined,
        }),
        {},
      );

      const createResponse = await createProvidedService({
        ...truthyAttrs,
        state: isFileCreationNeeded ? undefined : intendedState,
        metadata: otherMetadata,
      });
      const newProvidedServiceId = createResponse?.data?.data?.id;

      if (isFileCreationNeeded && newProvidedServiceId) {
        return updatePSIncludingFileUploads({
          id: newProvidedServiceId,
          metadata: [...otherMetadata, ...fileMetadata],
          state: intendedState,
        });
      }

      notifyOfSuccess(intendedState);
      invalidateQuery('provided_service');

      return createResponse;
    }, [createProvidedService, updatePSIncludingFileUploads, metafieldFiles, invalidateQuery]),
  };
};

export const useUpdateProvidedService = (providedServiceId) => {
  const { existingProvidedService, isSuccess: isPSSuccess } = useFindExistingProvidedService(providedServiceId);
  const {
    metafieldFiles,
    isSuccess: isFSPSuccess,
  } = useFSPMetaFiles(existingProvidedService?.program?.fee_schedule_program?.id);
  const updatePSIncludingFiles = useUpdatePSIncludingFileUploads(metafieldFiles, existingProvidedService?.metadata);

  return {
    isReady: isFSPSuccess && isPSSuccess,
    updateProvidedService: useCallback((updates) => {
      const actualUpdates = Object.entries(updates).reduce(
        (acc, [updatedKey, updatedVal]) => ({
          ...acc,
          [updatedKey]: existingProvidedService[updatedKey] !== updatedVal ? updatedVal : undefined,
        }),
        {},
      );
      return updatePSIncludingFiles({
        ...actualUpdates,
        id: providedServiceId,
        metadata: updates.metadata,
      });
    }, [existingProvidedService, updatePSIncludingFiles]),
  };
};
